import { useTranslation } from 'react-i18next';

export default function SchedulingShowcase() {
  const { t } = useTranslation();
  
  // Define times array directly to avoid translation issues
  const times = ['09:00', '11:30', '13:00', '14:30', '16:00', '17:30'];
  
  return (
    <section className="py-24 px-6 bg-gray-950 relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 right-1/4 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-1/4 w-96 h-96 bg-purple-500 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full mb-6">
            <i className="ri-calendar-check-fill text-blue-400"></i>
            <span className="text-blue-300 text-sm font-medium">{t('scheduling.badge')}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('scheduling.title')}
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            {t('scheduling.subtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* WhatsApp Conversation */}
          <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-3xl p-8 shadow-2xl h-full">
            <div className="bg-gray-800/50 rounded-2xl p-4 mb-6 flex items-center gap-3">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
              </div>
              <span className="text-gray-400 text-sm font-medium">{t('scheduling.badge')}</span>
            </div>

            <div className="space-y-4">
              {/* User Message */}
              <div className="flex justify-end">
                <div className="bg-green-600 text-white rounded-2xl rounded-tr-sm px-5 py-3 max-w-xs">
                  <p className="text-sm">{t('scheduling.chat.user1')}</p>
                </div>
              </div>

              {/* Bot Response */}
              <div className="flex gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-purple-600 to-green-600 rounded-full flex-shrink-0">
                  <i className="ri-robot-2-fill text-white text-lg"></i>
                </div>
                <div className="bg-gray-800 text-white rounded-2xl rounded-tl-sm px-5 py-3 max-w-md">
                  <p className="text-sm mb-2">{t('scheduling.chat.bot1')}</p>
                </div>
              </div>

              {/* User Message */}
              <div className="flex justify-end">
                <div className="bg-green-600 text-white rounded-2xl rounded-tr-sm px-5 py-3 max-w-xs">
                  <p className="text-sm">{t('scheduling.chat.user2')}</p>
                </div>
              </div>

              {/* Bot Response with Schedule */}
              <div className="flex gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-purple-600 to-green-600 rounded-full flex-shrink-0">
                  <i className="ri-robot-2-fill text-white text-lg"></i>
                </div>
                <div className="bg-gray-800 text-white rounded-2xl rounded-tl-sm px-5 py-4 max-w-md">
                  <p className="text-sm mb-3">{t('scheduling.chat.bot2')}</p>
                  <div className="space-y-2">
                    {times.map((time: string, idx: number) => (
                      <div key={idx} className="text-sm text-blue-300">• {time}</div>
                    ))}
                  </div>
                  <p className="text-sm mt-3 text-gray-400">{t('scheduling.chat.bot2b')}</p>
                </div>
              </div>

              {/* User Message */}
              <div className="flex justify-end">
                <div className="bg-green-600 text-white rounded-2xl rounded-tr-sm px-5 py-3 max-w-xs">
                  <p className="text-sm">{t('scheduling.chat.user3')}</p>
                </div>
              </div>

              {/* Bot Confirmation */}
              <div className="flex gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-purple-600 to-green-600 rounded-full flex-shrink-0">
                  <i className="ri-robot-2-fill text-white text-lg"></i>
                </div>
                <div className="bg-gray-800 text-white rounded-2xl rounded-tl-sm px-5 py-4 max-w-md">
                  <p className="text-sm mb-3">{t('scheduling.chat.bot3')}</p>
                  <div className="bg-blue-900/30 border border-blue-500/30 rounded-xl p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <i className="ri-calendar-check-fill text-blue-400"></i>
                      <span className="text-sm font-semibold">{t('scheduling.chat.confirmation.title')}</span>
                    </div>
                    <div className="text-sm text-gray-300">{t('scheduling.chat.confirmation.service')}</div>
                    <div className="text-sm text-gray-300">{t('scheduling.chat.confirmation.date')}</div>
                    <div className="text-sm text-gray-300">{t('scheduling.chat.confirmation.time')}</div>
                    <div className="text-sm text-gray-300">{t('scheduling.chat.confirmation.location')}</div>
                    <div className="text-xs text-blue-400 mt-2">{t('scheduling.chat.confirmation.reminder')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Features List */}
          <div className="space-y-8">
            <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-8 hover:border-blue-500/50 transition-all duration-300 cursor-pointer h-full">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl mb-6">
                <i className="ri-calendar-schedule-fill text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">
                <i className="ri-palette-fill text-purple-400 mr-2"></i>
                {t('scheduling.features.publicPage.title')}
              </h3>
              <p className="text-gray-400 leading-relaxed mb-4">
                {t('scheduling.features.publicPage.description')}
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <i className="ri-palette-fill text-purple-400 mt-1"></i>
                  <div>
                    <div className="text-white font-semibold">{t('scheduling.features.publicPage.customization.title')}</div>
                    <div className="text-sm text-gray-400">{t('scheduling.features.publicPage.customization.description')}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-global-fill text-purple-400 mt-1"></i>
                  <div>
                    <div className="text-white font-semibold">{t('scheduling.features.publicPage.publicAccess.title')}</div>
                    <div className="text-sm text-gray-400">{t('scheduling.features.publicPage.publicAccess.description')}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-settings-3-fill text-green-400 mt-1"></i>
                  <div>
                    <div className="text-white font-semibold">{t('scheduling.features.publicPage.configurable.title')}</div>
                    <div className="text-sm text-gray-400">{t('scheduling.features.publicPage.configurable.description')}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-8 hover:border-purple-500/50 transition-all duration-300 cursor-pointer">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl mb-6">
                <i className="ri-time-fill text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">{t('scheduling.features.availability.title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('scheduling.features.availability.description')}
              </p>
            </div>

            <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-8 hover:border-green-500/50 transition-all duration-300 cursor-pointer">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl mb-6">
                <i className="ri-settings-3-fill text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">{t('scheduling.features.configurable.title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('scheduling.features.configurable.description')}
              </p>
            </div>

            <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-8 hover:border-yellow-500/50 transition-all duration-300 cursor-pointer">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl mb-6">
                <i className="ri-notification-3-fill text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">{t('scheduling.features.reminders.title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('scheduling.features.reminders.description')}
              </p>
            </div>

            <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-8 hover:border-indigo-500/50 transition-all duration-300 cursor-pointer">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl mb-6">
                <i className="ri-clipboard-fill text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">{t('scheduling.features.control.title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('scheduling.features.control.description')}
              </p>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <div className="mt-16 text-center">
          <button className="px-10 py-5 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-lg font-semibold rounded-xl hover:scale-105 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/50 whitespace-nowrap cursor-pointer">
            <span className="flex items-center gap-3">
              <i className="ri-send-plane-fill text-xl"></i>
              {t('scheduling.cta')}
            </span>
          </button>
        </div>
      </div>
    </section>
  );
}
